import { useLayoutEffect, useRef, type ReactNode } from 'react';

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  disabled?: boolean | undefined;
  /** Accessible name when `label` is not plain text. */
  ariaLabel?: string | undefined;
}

/** Toggle buttons (aria-pressed) with a sliding indicator measured from the pressed button. */
export function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
  small,
  className,
}: {
  label: string;
  value: T | null;
  options: SegmentOption<T>[];
  onChange: (value: T) => void;
  small?: boolean | undefined;
  className?: string | undefined;
}) {
  const box = useRef<HTMLDivElement>(null);
  const ind = useRef<HTMLSpanElement>(null);
  const placed = useRef(false);

  useLayoutEffect(() => {
    const el = box.current;
    const bar = ind.current;
    if (!el || !bar) return;
    const place = () => {
      const on = el.querySelector<HTMLElement>('button[aria-pressed="true"]');
      if (!on || on.offsetWidth === 0) {
        bar.style.opacity = on ? bar.style.opacity : '0';
        return;
      }
      const instant = !placed.current;
      if (instant) bar.style.transition = 'none';
      bar.style.opacity = '1';
      bar.style.left = `${on.offsetLeft}px`;
      bar.style.width = `${on.offsetWidth}px`;
      if (instant) {
        void bar.offsetWidth;
        bar.style.transition = '';
      }
      placed.current = true;
    };
    place();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(place);
    ro.observe(el);
    return () => ro.disconnect();
  }, [value]);

  return (
    <div
      ref={box}
      className={`pf-segctl${small ? ' pf-segctl-sm' : ''}${className ? ` ${className}` : ''}`}
      role="group"
      aria-label={label}
    >
      <span ref={ind} className="pf-ind" aria-hidden="true" style={{ opacity: 0 }} />
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          aria-label={o.ariaLabel}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
